import { GP_VIEW_PERMISSION } from '@supply-mover-2-0/shared';
import type { ColumnConfiguration, ListBuilderConfiguration } from '../types';

/**
 * Payment status value mapping shared across invoice lists
 */
const PAYMENT_STATUS_VALUE_MAPPING = [
	{
		value: 'Outstanding',
		displayText: 'Outstanding',
		color: { type: 'warning' },
	},
	{
		value: 'Paid in Full',
		displayText: 'Paid in Full',
		color: { type: 'success' },
	},
	{
		value: 'Cancelled',
		displayText: 'Cancelled',
		color: { type: 'danger' },
	},
	// PD-16747 — the values `deriveInvoicePaymentStatus` returns besides the
	// three above. Without an entry the badge fell back to an unstyled raw
	// value, so a credit memo's state and a Closed invoice's read like noise.
	{
		value: 'Closed',
		displayText: 'Closed',
		color: { type: 'neutral' },
	},
	{
		value: 'Credit Outstanding',
		displayText: 'Credit Outstanding',
		color: { type: 'info' },
	},
	{
		value: 'Credit Applied',
		displayText: 'Credit Applied',
		color: { type: 'neutral' },
	},
];

/**
 * Shared column definitions for invoice embedded lists
 */
const getInvoiceNumberColumn = () => ({
	id: 'invoice-number',
	fields: [
		{
			id: 'invoice-external-id-field',
			link: {
				type: 'object' as const,
				enabled: true,
				idField: '_id',
				objectTable: 'invoice',
			},
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'invoiceNumber', objectTable: 'invoice' },
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: {
					type: 'theme' as const,
					themeColor: 'statusInfo',
				},
			},
			displayStyle: 'text' as const,
		},
	],
	headers: [{ id: 'h1-invoice', text: 'Invoice #' }],
	minWidth: 120,
	sortable: true,
	alignment: 'left' as const,
	sortFields: [
		{
			direction: 'ASC' as const,
			fieldName: 'externalId',
			objectTable: 'invoice',
		},
	],
	widthConfig: { mode: 'flexible' as const, preset: 'small' as const },
	verticalAlignment: 'top' as const,
});

const getDateColumn = (): ColumnConfiguration => ({
	id: 'date',
	fields: [
		{
			id: 'date-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'date', objectTable: 'invoice' },
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
				formatOptions: { dateFormat: 'short' },
			},
			displayStyle: 'date' as const,
		},
	],
	headers: [{ id: 'h3', text: 'Date' }],
	minWidth: 80,
	sortable: true,
	alignment: 'left' as const,
	sortFields: [
		{ direction: 'DESC' as const, fieldName: 'date', objectTable: 'invoice' },
	],
	widthConfig: { mode: 'flexible' as const, preset: 'small' as const },
	verticalAlignment: 'top' as const,
});

const getSalespersonColumn = () => ({
	id: 'salesperson',
	fields: [
		{
			id: 'salesperson-name-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'salesRepFirstName', objectTable: 'invoice' },
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'text' as const,
		},
	],
	headers: [{ id: 'h-salesperson', text: 'Sales Rep' }],
	minWidth: 100,
	sortable: false,
	alignment: 'left' as const,
	widthConfig: { mode: 'flexible' as const, preset: 'small' as const },
	verticalAlignment: 'top' as const,
});

const getLinesColumn = () => ({
	id: 'lines',
	fields: [
		{
			id: 'lines-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'numberOfLines', objectTable: 'invoice' },
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'text' as const,
		},
	],
	headers: [{ id: 'h4', text: 'Lines' }],
	minWidth: 60,
	sortable: true,
	alignment: 'center' as const,
	sortFields: [
		{
			direction: 'DESC' as const,
			fieldName: 'numberOfLines',
			objectTable: 'invoice',
		},
	],
	widthConfig: { mode: 'flexible' as const, preset: 'small' as const },
	verticalAlignment: 'top' as const,
});

const getAdjustmentsColumn = () => ({
	id: 'adjustments',
	fields: [
		{
			id: 'tax-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'tax', objectTable: 'invoice' },
			},
			styleConfig: {
				size: 'xs' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textPrimary' },
			},
			displayStyle: 'currency' as const,
		},
		{
			id: 'line-break-tax',
			dataConfig: { type: 'custom_text' as const, customText: '' },
			styleConfig: {
				size: 'md' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'line_break' as const,
		},
		{
			id: 'freight-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'freight', objectTable: 'invoice' },
			},
			styleConfig: {
				size: 'xs' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
			},
			displayStyle: 'currency' as const,
		},
		{
			id: 'line-break-freight',
			dataConfig: { type: 'custom_text' as const, customText: '' },
			styleConfig: {
				size: 'md' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'line_break' as const,
		},
		{
			id: 'discount-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'discount', objectTable: 'invoice' },
			},
			styleConfig: {
				size: 'xs' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
			},
			displayStyle: 'currency' as const,
		},
	],
	headers: [
		{ id: 'h-tax', text: 'Tax' },
		{ id: 'h-freight', text: 'Freight' },
		{ id: 'h-discount', text: 'Discount' },
	],
	minWidth: 90,
	sortable: true,
	alignment: 'right' as const,
	sortFields: [
		{ direction: 'DESC' as const, fieldName: 'tax', objectTable: 'invoice' },
	],
	widthConfig: { mode: 'flexible' as const, preset: 'small' as const },
	verticalAlignment: 'top' as const,
});

const getInvoiceTotalsColumn = () => ({
	id: 'invoice-totals',
	fields: [
		{
			id: 'subtotal-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'subtotal', objectTable: 'invoice' },
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textPrimary' },
			},
			displayStyle: 'currency' as const,
		},
		{
			id: 'line-break-5',
			dataConfig: { type: 'custom_text' as const, customText: '' },
			styleConfig: {
				size: 'md' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'line_break' as const,
		},
		{
			id: 'total-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'total', objectTable: 'invoice' },
			},
			styleConfig: {
				size: 'md' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
			},
			displayStyle: 'currency' as const,
		},
		{
			id: 'line-break-6',
			dataConfig: { type: 'custom_text' as const, customText: '' },
			styleConfig: {
				size: 'md' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'line_break' as const,
		},
		{
			id: 'amount-owed-field',
			dataConfig: {
				type: 'field' as const,
				field: {
					fieldName: 'm_sm_payment_outstandingBalance',
					objectTable: 'invoice',
				},
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
			},
			displayStyle: 'currency' as const,
		},
	],
	headers: [
		{ id: 'h6-subtotal', text: 'Subtotal' },
		{ id: 'h6-total', text: 'Total' },
		{ id: 'h6-amount-owed', text: 'Amount Owed' },
	],
	minWidth: 120,
	sortable: true,
	alignment: 'right' as const,
	sortFields: [
		{ direction: 'DESC' as const, fieldName: 'total', objectTable: 'invoice' },
	],
	widthConfig: { mode: 'flexible' as const, preset: 'medium' as const },
	verticalAlignment: 'top' as const,
});

const getGpTotalsColumn = () => ({
	id: 'gp-totals',
	fields: [
		{
			id: 'total-gp-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'totalGP', objectTable: 'invoice' },
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textPrimary' },
			},
			displayStyle: 'currency' as const,
			requiredPermission: GP_VIEW_PERMISSION,
		},
	],
	headers: [{ id: 'h8-total-gp', text: 'GP' }],
	minWidth: 120,
	sortable: true,
	alignment: 'right' as const,
	sortFields: [
		{
			direction: 'DESC' as const,
			fieldName: 'totalGP',
			objectTable: 'invoice',
		},
	],
	widthConfig: { mode: 'flexible' as const, preset: 'medium' as const },
	verticalAlignment: 'top' as const,
});

const getPaymentStatusColumn = () => ({
	id: 'payment-status',
	fields: [
		{
			id: 'payment-status-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'paymentStatus', objectTable: 'invoice' },
			},
			styleConfig: {
				size: 'xs' as const,
				valueMapping: PAYMENT_STATUS_VALUE_MAPPING,
			},
			displayStyle: 'badge' as const,
		},
	],
	headers: [{ id: 'h7', text: 'Payment Status' }],
	minWidth: 120,
	sortable: false,
	alignment: 'center' as const,
	widthConfig: { mode: 'flexible' as const, preset: 'medium' as const },
	verticalAlignment: 'center' as const,
});

/**
 * Invoice Client Embedded list configuration
 * Multi-view: Outstanding, Paid, All Invoices
 * Converted from JSON to TypeScript for type safety
 */
export const INVOICE_CLIENT_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'client-embedded-invoices-outstanding',
			cacheTTL: 600000,
			name: 'Outstanding',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				getInvoiceNumberColumn(),
				getDateColumn(),
				getSalespersonColumn(),
				getLinesColumn(),
				getAdjustmentsColumn(),
				getInvoiceTotalsColumn(),
				getGpTotalsColumn(),
				getPaymentStatusColumn(),
			],
			description: 'Invoices with outstanding payments',
			defaultSortId: 'column-date-asc',
			requiredFields: [
				'_id',
				'externalId',
				'externalId2',
				'invoiceNumber',
				'date',
				'_fk_client__id',
				'fk_company__id',
				'total',
				'companyName',
				'm_sm_payment_outstandingBalance',
				'paymentStatus',
			],
			filterRules: {
				invoicePaymentStatus: {
					defaultValue: [{ id: 'outstanding', label: 'Outstanding' }],
				},
			},
		},
		{
			id: 'client-embedded-invoices-paid',
			cacheTTL: 600000,
			name: 'Paid',
			type: 'default',
			active: true,
			sortOrder: 1,
			columns: [
				getInvoiceNumberColumn(),
				getDateColumn(),
				getSalespersonColumn(),
				getLinesColumn(),
				getAdjustmentsColumn(),
				getInvoiceTotalsColumn(),
				getGpTotalsColumn(),
				getPaymentStatusColumn(),
			],
			description: 'Invoices that have been paid in full',
			defaultSortId: 'column-date-desc',
			requiredFields: [
				'_id',
				'externalId',
				'externalId2',
				'invoiceNumber',
				'date',
				'_fk_client__id',
				'fk_company__id',
				'total',
				'salesRepFirstName',
				'companyName',
				'paymentStatus',
			],
			filterRules: {
				invoicePaymentStatus: {
					defaultValue: [{ id: 'paid_in_full', label: 'Paid in Full' }],
				},
			},
		},
		{
			id: 'client-embedded-invoices-all',
			cacheTTL: 600000,
			name: 'All Invoices',
			type: 'default',
			active: true,
			sortOrder: 2,
			columns: [
				getInvoiceNumberColumn(),
				getDateColumn(),
				getSalespersonColumn(),
				getLinesColumn(),
				getAdjustmentsColumn(),
				getInvoiceTotalsColumn(),
				getGpTotalsColumn(),
				getPaymentStatusColumn(),
			],
			description: 'Complete list of all invoices with key details',
			defaultSortId: 'column-date-desc',
			requiredFields: [
				'_id',
				'externalId',
				'externalId2',
				'invoiceNumber',
				'date',
				'_fk_client__id',
				'fk_company__id',
				'numberOfLines',
				'tax',
				'freight',
				'discount',
				'subtotal',
				'total',
				'salesRepFirstName',
				'companyName',
				'm_sm_payment_outstandingBalance',
				'paymentStatus',
				'totalGP',
			],
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [20, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			// PD-14788: invoiceCustomerPO is deliberately not a default filter —
			// the general search matches PO now. It remains available through the
			// Configure List Filters tab.
			'childAccounts',
			'invoiceKeywords',
			'invoiceCompany',
			'invoiceDateRange',
			'invoiceSalesRep',
			'invoicePaymentStatus',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/invoices/:id',
			objectLabel: 'Invoice',
			objectTable: 'invoice',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'externalId2', fieldLabel: 'External ID 2' },
				{ fieldName: 'date', fieldLabel: 'Date' },
				{ fieldName: '_fk_client__id', fieldLabel: 'Client ID' },
				{ fieldName: 'fk_company__id', fieldLabel: 'Company ID' },
				{ fieldName: 'numberOfLines', fieldLabel: 'Number of Lines' },
				{ fieldName: 'tax', fieldLabel: 'Tax' },
				{ fieldName: 'freight', fieldLabel: 'Freight' },
				{ fieldName: 'discount', fieldLabel: 'Discount' },
				{ fieldName: 'subtotal', fieldLabel: 'Subtotal' },
				{ fieldName: 'total', fieldLabel: 'Total' },
				{ fieldName: 'salesRepFirstName', fieldLabel: 'Sales Rep First Name' },
				// PD-14788: derived from the invoice's orders by the backend, not an
				// invoice column. Offered here so it can be added through Configure
				// List; no list ships it as a visible column.
				{ fieldName: 'customerPO', fieldLabel: 'Customer PO' },
			],
		},
	],
	tableSorts: [
		{
			id: 'date-desc',
			name: 'Date (Newest First)',
			fields: [
				{ direction: 'DESC', fieldName: 'date', objectTable: 'invoice' },
			],
			description: 'Sort by invoice date descending',
		},
		{
			id: 'date-asc',
			name: 'Date (Oldest First)',
			fields: [{ direction: 'ASC', fieldName: 'date', objectTable: 'invoice' }],
			description: 'Sort by invoice date ascending',
		},
	],
};
